import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Row, Col, Avatar, Typography, Empty, Badge, Button, Tooltip } from 'antd';
import { GiftOutlined, CalendarOutlined, StarFilled, PhoneOutlined } from '@ant-design/icons';
import { SERVER_IP } from 'assets/Config';
import { DATE_FORMAT } from 'constants/app-constants';
import { getDateFormat } from 'services/Utils';
import { fetchCustomerImage } from 'services/customerImages';
import { useSelector } from 'react-redux';
import MaleSVG from '../../../assets/images/male.svg';
import FemaleSVG from '../../../assets/images/female.svg';
import './upcoming-birthdays.scss';

const { Text, Title } = Typography;

const ConfettiParticles = () => {
	return (
		<div className="confetti-container">
			{Array.from({ length: 20 }).map((_, i) => (
				<div
					key={i}
					className="confetti"
					style={{
						left: `${Math.random() * 100}%`,
						animationDelay: `${Math.random() * 2}s`,
						backgroundColor: ['#ff6b9d', '#ffa726', '#66bb6a', '#4d7cfe', '#ffeb3b'][Math.floor(Math.random() * 5)],
					}}
				/>
			))}
		</div>
	);
};

const UpcomingBirthdays = ({ birthdays = [], loading = false }) => {
	const sortedBirthdays = useMemo(() => {
		if (!birthdays || !Array.isArray(birthdays) || birthdays.length === 0) return [];
		
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		
		const validBirthdays = birthdays
			.map((customer) => {
				if (!customer || !customer.dob) return null;
				
				const dob = new Date(customer.dob);
				if (isNaN(dob.getTime())) return null;
				
				const thisYearBirthday = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
				const nextYearBirthday = new Date(today.getFullYear() + 1, dob.getMonth(), dob.getDate());
				
				let nextBirthday = thisYearBirthday < today ? nextYearBirthday : thisYearBirthday;
				const daysUntil = Math.ceil((nextBirthday - today) / (1000 * 60 * 60 * 24));
				
				return {
					...customer,
					daysUntil,
					nextBirthday,
					isToday: daysUntil === 0,
					isTomorrow: daysUntil === 1,
				};
			})
			.filter(Boolean)
			.sort((a, b) => a.daysUntil - b.daysUntil)
			.slice(0, 5); // Show top 5 upcoming birthdays
		
		return validBirthdays;
	}, [birthdays]);

	if (loading) {
		return (
			<Row className="upcoming-birthdays-card" gutter={[0, 16]}>
				<Col span={24}>
					<div className="birthday-skeleton">
						<div className="skeleton-avatar"></div>
						<div className="skeleton-content">
							<div className="skeleton-line"></div>
							<div className="skeleton-line short"></div>
						</div>
					</div>
				</Col>
			</Row>
		);
	}

	if (!sortedBirthdays || sortedBirthdays.length === 0) {
		return (
			<Row className="upcoming-birthdays-card">
				<Col span={24}>
					<div className="birthday-header">
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<GiftOutlined style={{ fontSize: '14px', color: '#ff6b9d' }} />
							<span>Upcoming Birthdays</span>
						</div>
					</div>
					<div className="empty-state">
						<Empty 
							description={<span className="empty-text">No upcoming birthdays</span>} 
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							imageStyle={{ opacity: 0.5 }}
						/>
					</div>
				</Col>
			</Row>
		);
	}

	const todayCount = sortedBirthdays.filter(b => b.isToday).length;

	return (
		<Row className="upcoming-birthdays-card">
			<Col span={24}>
				<div className="birthday-header">
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<GiftOutlined style={{ fontSize: '14px', color: '#ff6b9d' }} />
						<span>Upcoming Birthdays</span>
						{todayCount > 0 && (
							<Badge 
								count={todayCount} 
								style={{ backgroundColor: '#ff6b9d', marginLeft: 8 }}
								className="today-badge"
							/>
						)}
					</div>
				</div>
				{sortedBirthdays.some(b => b.isToday) && <ConfettiParticles />}
				<Row gutter={[0, 12]} className="birthday-list">
					{sortedBirthdays.map((customer, index) => (
						<BirthdayItem key={customer.id || index} customer={customer} index={index} />
					))}
				</Row>
			</Col>
		</Row>
	);
};

const BirthdayItem = ({ customer, index }) => {
	const { daysUntil, isToday, isTomorrow, nextBirthday } = customer;
	const loginRedux = useSelector((state) => state.loginRedux);
	const [imageUrl, setImageUrl] = useState(null);
	const imageUrlRef = useRef(null);

	const clearImageUrl = useCallback((updateState = true) => {
		if (imageUrlRef.current) {
			URL.revokeObjectURL(imageUrlRef.current);
			imageUrlRef.current = null;
		}
		if (updateState) {
			setImageUrl(null);
		}
	}, []);

	useEffect(() => {
		let isMounted = true;

		const loadImage = async () => {
			clearImageUrl();
			if (!customer?.id || !loginRedux?.token) return;

			const result = await fetchCustomerImage(customer.id, loginRedux.token);

			if (!isMounted) {
				if (result?.data) {
					URL.revokeObjectURL(result.data);
				}
				return;
			}

			if (result?.data) {
				imageUrlRef.current = result.data;
				setImageUrl(result.data);
			} else if (result?.error) {
				console.error('Failed to load customer image', result.error);
			}
		};

		loadImage();

		return () => {
			isMounted = false;
			clearImageUrl(false);
		};
	}, [clearImageUrl, customer?.id, loginRedux?.token]);
	
	const getStatusText = () => {
		if (isToday) return { 
			text: "Today!", 
			color: '#ff6b9d', 
			className: 'today',
			icon: '🎂',
			badge: 'HAPPY BIRTHDAY!'
		};
		if (isTomorrow) return { 
			text: 'Tomorrow', 
			color: '#ffa726', 
			className: 'tomorrow',
			icon: '🎁'
		};
		if (daysUntil <= 7) return { 
			text: `In ${daysUntil} days`, 
			color: '#66bb6a', 
			className: 'this-week',
			icon: '🎈'
		};
		return { 
			text: `In ${daysUntil} days`, 
			color: '#90a4ae', 
			className: 'upcoming',
			icon: '📅'
		};
	};

	const status = getStatusText();
	const defaultAvatar = customer.gender === 'female' ? FemaleSVG : MaleSVG;
	const existingImage = customer.image?.[0]?.url ? SERVER_IP + customer.image[0].url : null;
	const avatarSrc = imageUrl || existingImage || defaultAvatar;

	return (
		<Col span={24}>
			<div className={`birthday-item ${status.className}`}>
				<div className="birthday-avatar-wrapper">
					<div className="avatar-ring">
						<Avatar
							src={avatarSrc}
							size={52}
							icon={<GiftOutlined />}
							className="birthday-avatar"
						/>
					</div>
					{isToday && (
						<>
							<div className="celebration-badge">🎂</div>
							<div className="sparkle-icon sparkle-top">✨</div>
							<div className="sparkle-icon sparkle-bottom">⭐</div>
						</>
					)}
				</div>
				<div className="birthday-content">
					{isToday && (
						<div className="birthday-banner">
							<StarFilled className="star-icon" />
							<span>{status.badge}</span>
							<StarFilled className="star-icon" />
						</div>
					)}
					<div className="birthday-name-row">
						<Text strong className="birthday-name">
							{customer.customerName || 'Unknown'}
						</Text>
						<span className="status-icon">{status.icon}</span>
					</div>
					<div className="birthday-meta">
						<CalendarOutlined className="calendar-icon" />
						<Text className="birthday-date">
							{getDateFormat(nextBirthday, DATE_FORMAT.DD_MM_YYYY)}
						</Text>
						<span className="birthday-status" style={{ color: status.color }}>
							• {status.text}
						</span>
					</div>
				</div>
				{customer.mobile && (
					<div className="birthday-actions">
						<Tooltip title={`Call ${customer.customerName}`} placement="top">
							<button
								className={`call-button ${isToday ? 'call-button-today' : ''}`}
								onClick={() => {
									window.location.href = `tel:+91${customer.mobile.replace(/\D/g, '')}`;
								}}
								aria-label={`Call ${customer.customerName}`}
							>
								<PhoneOutlined />
							</button>
						</Tooltip>
					</div>
				)}
			</div>
		</Col>
	);
};

export default UpcomingBirthdays;
